import React, { useCallback, useEffect, useMemo, useState } from 'react';
import CancelIcon from '@material-ui/icons/Cancel';
import FirstPageIcon from '@material-ui/icons/FirstPage';
import SearchIcon from '@material-ui/icons/Search';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { Box, IconButton, InputAdornment } from '@material-ui/core';
import FilterOption, { FilterOptionProps } from '../FilterOption';
import {
  FilterContainer,
  FilterTitle,
  ChipsContainer,
  StyledChip,
  SectionTitle,
  EmptyMessage,
  FilterHeader,
  FilterHeaderActions,
  ClearAllButton,
  SearchSection,
  SearchLabel,
  SearchField,
  SectionHeader,
} from './SidebarFilters.styles';

const SEARCH_DEBOUNCE_MS = 300;

export interface FilterOptionData extends Omit<FilterOptionProps, 'onChange'> {
  id: string;
  label: string;
  qty: number;
}

export interface SidebarFiltersProps {
  options: FilterOptionData[];
  selectedOptions?: string[];
  searchQuery?: string;
  onSelectionChange?: (selectedIds: string[]) => void;
  onSearchChange?: (search: string) => void;
  onClearAll?: () => void;
  onClose?: () => void;
}

const SidebarFilters = ({
  options = [],
  selectedOptions = [],
  searchQuery = '',
  onSelectionChange,
  onSearchChange,
  onClearAll,
  onClose,
}: SidebarFiltersProps) => {
  const [apiTypesOpen, setApiTypesOpen] = useState(true);
  const [localSearch, setLocalSearch] = useState(searchQuery);

  useEffect(() => {
    setLocalSearch(searchQuery);
  }, [searchQuery]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      if (localSearch === searchQuery) return;
      onSearchChange?.(localSearch);
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(handle);
  }, [localSearch, searchQuery, onSearchChange]);

  const hasActiveFilters = selectedOptions.length > 0 || Boolean(searchQuery);

  const sortedOptions = useMemo(
    () => [...options].sort((a, b) => b.qty - a.qty),
    [options],
  );

  const removeApiTypeFilter = useCallback(
    (apiTypeToRemove: string) => {
      onSelectionChange?.(selectedOptions.filter(id => id !== apiTypeToRemove));
    },
    [onSelectionChange, selectedOptions],
  );

  const handleOptionChange = useCallback(
    (optionId: string, isChecked: boolean) => {
      if (!onSelectionChange) return;
      onSelectionChange(
        isChecked
          ? [...selectedOptions, optionId]
          : selectedOptions.filter(id => id !== optionId),
      );
    },
    [onSelectionChange, selectedOptions],
  );

  const maxValue = useMemo(
    () => Math.max(...sortedOptions.map(option => option.qty), 0),
    [sortedOptions],
  );

  return (
    <FilterContainer>
      <FilterHeader>
        <FilterTitle variant="h6">Filter Results</FilterTitle>
        <FilterHeaderActions>
          {hasActiveFilters && (
            <ClearAllButton
              color="primary"
              onClick={() => onClearAll?.()}
              size="small"
            >
              CLEAR ALL
            </ClearAllButton>
          )}
          <IconButton
            onClick={onClose}
            aria-label="Collapse filters"
            size="small"
          >
            <FirstPageIcon color="primary" />
          </IconButton>
        </FilterHeaderActions>
      </FilterHeader>

      {hasActiveFilters && (
        <ChipsContainer>
          {searchQuery ? (
            <StyledChip
              key="search-query"
              label={`Search: "${searchQuery}"`}
              variant="default"
              onDelete={() => onSearchChange?.('')}
              deleteIcon={<CancelIcon />}
              size="small"
            />
          ) : null}
          {selectedOptions.map(apiType => (
            <StyledChip
              key={apiType}
              label={apiType}
              color="primary"
              onDelete={() => removeApiTypeFilter(apiType)}
              deleteIcon={<CancelIcon />}
              size="small"
            />
          ))}
        </ChipsContainer>
      )}
      <SearchSection>
        <SearchLabel>Search</SearchLabel>
        <SearchField
          value={localSearch}
          onChange={event => setLocalSearch(event.target.value)}
          onKeyDown={event => {
            if (event.key === 'Enter') {
              event.preventDefault();
              onSearchChange?.(localSearch);
            }
          }}
          placeholder="Search by Asset Name..."
          variant="outlined"
          fullWidth
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
        />
      </SearchSection>

      <SectionHeader>
        <SectionTitle variant="body1">API Types</SectionTitle>
        <IconButton
          size="small"
          aria-label={apiTypesOpen ? 'Collapse API Types' : 'Expand API Types'}
          onClick={() => setApiTypesOpen(open => !open)}
        >
          {apiTypesOpen ? (
            <ExpandLessIcon color="primary" />
          ) : (
            <ExpandMoreIcon color="primary" />
          )}
        </IconButton>
      </SectionHeader>

      {apiTypesOpen && (
        <Box>
          {sortedOptions.length > 0 ? (
            sortedOptions.map(option => (
              <FilterOption
                key={option.id}
                label={option.label}
                value={option.qty}
                maxValue={maxValue}
                checked={selectedOptions.includes(option.id)}
                onChange={isChecked => handleOptionChange(option.id, isChecked)}
              />
            ))
          ) : (
            <EmptyMessage color="secondary">No options available</EmptyMessage>
          )}
        </Box>
      )}
    </FilterContainer>
  );
};

export default SidebarFilters;
